<template>
    <app-layout>
        <teleport defer to="#app-header-page-title">
            <page-title
                :heading-classes="promotedTitleHeadingClasses"
                :title="dashboardTitle"
                class="app-header-page-title w-full min-w-0"
                variant="inverse"
            />
        </teleport>
        <div class="dashboard-header sticky top-15 z-1 items-center gap-2 bg-body px-6 pt-6 pb-4 md:flex md:flex-wrap">
            <div :class="hasFilterbar ? '' : 'w-full'" class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <Tabs :tabs="availableDashboards" />
                    <button
                        v-if="hasFilterbar"
                        class="btn-text d-sm-none flex basis-full items-center gap-1"
                        style="min-height: 44px"
                        @click.stop="toggleFilterbar"
                    >
                        <spinner v-if="queryParams?.form?.processing" />
                        <icon v-else icon="cil-list-filter" style="position: relative; top: -1px" />
                        <span>{{ trans('general.table.filter.label') }}</span>
                    </button>
                </div>
                <div v-if="isDashboardEditable">
                    <div v-if="!isGridEditmodeActive" class="d-flex gap-5">
                        <div v-if="currentDashboard !== 'health'" class="text-small d-flex align-items-center text-muted">
                            {{ trans('dashboard.v2.last_update') }}: {{ formatLastUpdated(lastUpdatedAt) }}
                        </div>
                        <jet-button variant="secondary" @click.stop="editDashboard">
                            {{ trans('dashboard.edit_view') }} |
                            <icon icon="cil-pencil" />
                        </jet-button>
                    </div>
                    <div v-else>
                        <div id="page-header-action" />
                        <jet-form-buttons
                            :cancel-route="route('dashboard.index')"
                            :disabled="!form.isDirty"
                            :has-changes="form.isDirty"
                            :is-processing="form.processing"
                            :is-update-form="true"
                            @submit="handleSubmit"
                        />
                    </div>
                </div>
                <div v-else-if="!hasFilterbar" class="text-small flex flex-wrap items-center justify-end pr-2 text-muted md:pr-0">
                    <span class="whitespace-nowrap">{{ trans('dashboard.v2.last_update') }}:&nbsp;</span
                    ><span class="whitespace-nowrap">{{ formatLastUpdated(lastUpdatedAt) }}</span>
                </div>
            </div>
            <div v-if="hasFilterbar" class="mt-4 grow md:mt-0!">
                <div class="flex flex-wrap items-center justify-between gap-4">
                    <filter-bar
                        ref="filterBar"
                        v-model="queryParams"
                        v-model:is-open="filterIsOpen"
                        :config="filterBarConfig"
                        :teleport-popovers="true"
                        class="w-full"
                    >
                        <template #locations-option="{ option }">
                            <div class="flex items-center justify-between gap-3">
                                <span>{{ option.label }}</span>
                                <small class="text-muted">{{ option.iataCode }}</small>
                            </div>
                        </template>
                        <template #types-option="{ option }">
                            <div class="flex items-center justify-between gap-3">
                                <span>{{ option.label }}</span>
                                <small class="text-muted">{{ option.shortLabel }}</small>
                            </div>
                        </template>
                        <template v-if="hasGrouping" #addon>
                            <div class="flex grow items-center justify-end gap-2 overflow-hidden">
                                <label class="font-medium text-[#768192]" for="grouping">{{ trans('general.compare.label') }}</label>
                                <jet-select
                                    id="grouping"
                                    v-model="selectedGrouping"
                                    :formGroupClasses="[]"
                                    :horizontal="false"
                                    :options="availableGroupings?.data ?? []"
                                    :resettable="false"
                                    align="end"
                                />
                            </div>
                        </template>
                    </filter-bar>
                </div>
            </div>
        </div>
        <div v-if="hasFilterbar" class="small-screen-hint">
            <icon icon="cil-screen-desktop" />
            <span>{{ trans('dashboard.v2.small_screen_hint') }}</span>
        </div>
        <div>
            <grid-stack
                ref="gridStackRef"
                v-model:widgets="form.widgets"
                :editable="isGridEditmodeActive"
                :filters="queryParams"
                :stacked="usesStackedLayout"
            />
        </div>
    </app-layout>
</template>

<style scoped>
/*
    The filter-bar wrapper keeps `grow`, so it stays inline on the tabs row,
    filling the space beside the tabs until the row genuinely can't hold both
    (only then does flex-wrap drop it to its own line, near mobile). Within that
    inline bar the inputs always hug the right edge — `margin-left: auto` on the
    inner row pushes them flush-right with no tuned breakpoint, so the filters
    group at the body cards' right edge instead of left-clumping after the tabs.

    Target `.d-sm-flex`'s child row (not `.collapse.d-sm-flex`): CoreUI's
    CCollapse swaps `.collapse` for `.collapsing` mid-animation, so qualifying on
    `.collapse` would only align once the animation settles.
*/
.dashboard-header :deep(.filterbar .d-sm-flex > *) {
    margin-left: auto;
}

.small-screen-hint {
    display: none;
}

@media (orientation: portrait) and (max-width: 575px) {
    .small-screen-hint {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        margin-bottom: 0.75rem;
        background-color: #eff6ff;
        border: 1px solid #bfdbfe;
        border-radius: 0.375rem;
        font-size: 0.8125rem;
        color: #1d4ed8;
    }
}
</style>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import JetButton from '@/Jetstream/Button.vue';
import { DateTime } from 'luxon';
import { trans } from '@/mixins/i18n';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import GridStack from '@/Components/GridStack.vue';
import Tabs from './Components/Tabs.vue';
import JetFormButtons from '@/Jetstream/FormButtons.vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { eventBus, events } from '@/eventBus';
import FilterBar from '@/Components/FilterBar.vue';
import { route } from 'ziggy-js';
import Spinner from '@/Components/Spinner.vue';
import Icon from '@/Components/Icon.vue';
import JetSelect from '@/Jetstream/Select.vue';
import { formatLastUpdated } from '@/Utils/formatLastUpdated.ts';
import PageTitle from '@/Components/PageTitle.vue';
import { useComplianceShift } from '@/composables/useComplianceShift';

const props = defineProps({
    title: String,
    availableGroupings: Object,
    activeGrouping: String,
});

const isGridEditmodeActive = ref(false);

const page = usePage();
const availableDashboards = computed(() => page.props.availableDashboards);

// Mutable copy for useForm so GridStack update:widgets doesn't hit readonly page.props
const initialWidgets = () => JSON.parse(JSON.stringify(page.props.widgets ?? []));
const isDashboardEditable = computed(() => page.props.isEditable);
const currentDashboard = computed(() => page.props.currentDashboard);
const filterOptions = computed(() => page.props.filterOptions || {});
const hasFilterbar = computed(() => {
    const value = filterOptions.value;
    const hasOptions = value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0;
    // Hide the whole bar when every multi-select would be dropped by FilterBar (<= 1 option).
    return hasOptions && filterBarConfig.value.filters.some((f) => f.type !== 'multi-select' || (Array.isArray(f.options) && f.options.length > 1));
});

const hasGrouping = computed(() => {
    return currentDashboard.value === 'health';
});
const dashboardTitle = computed(() => props.title || trans('dashboard.headline'));
const promotedTitleHeadingClasses =
    '!mb-0 !min-h-0 overflow-hidden text-ellipsis whitespace-nowrap text-left !text-base !leading-tight min-[768px]:text-center min-[768px]:!text-2xl min-[768px]:!leading-[1.25]';

const usesStackedLayout = computed(() => page.props.currentDashboardUsesStackedLayout === true);

// Global shift switcher for the testing-compliance dashboard: one control in the
// page header drives both the donuts and the by-line table. Options are
// published by the by-type widget; `selectedShift = null` follows the current.
// The compliance shift filter: options are published by the by-type widget to a
// shared composable; the value lives in the query params, so every compliance
// widget consumes it like any other filter.
const { shiftMeta: complianceShiftMeta } = useComplianceShift();
// Shift switcher options come ready-shaped from the backend ({ value: key,
// label: translated display }); the value is the stable key sent as `?shift=`.
const shiftOptions = computed(() => complianceShiftMeta.value?.options ?? []);

const locations = computed(() =>
    (filterOptions.value.locations?.data || []).map((loc) => ({
        label: loc.name,
        value: loc.icaoCode,
        iataCode: loc.iataCode,
        searchableContent: `${loc.iataCode}|${loc.icaoCode}|${loc.name}`,
    })),
);
const areas = computed(() => (filterOptions.value.areas?.data || []).map((area) => ({ label: area.name, value: area.id })));

const types = computed(() => filterOptions.value.types?.data || []);
const maintainers = computed(() =>
    (filterOptions.value.maintainers?.data ?? filterOptions.value.maintainers ?? []).map((m) => ({
        label: m.name,
        value: m.id,
    })),
);
const filterBar = ref(null);
const queryParams = ref(null);
const filterIsOpen = ref(false);
const selectedGrouping = ref(props.activeGrouping || 'asset');

const filterBarConfig = computed(() => ({
    formData: {
        grouping: selectedGrouping.value,
    },
    // Compliance dashboard only filters by airport today; the other filter
    // types aren't wired through its aggregation services yet.
    // Preventative Maintenance scopes by maintainer and asset type only.
    filters: [
        {
            type: 'multi-select',
            key: 'locations',
            label: trans('dashboard.filter.locations'),
            options: locations.value,
            showSearchbar: true,
        },
        {
            type: 'multi-select',
            key: 'areas',
            label: trans('locations.area.label'),
            options: areas.value,
            showSearchbar: true,
        },
        {
            type: 'multi-select',
            key: 'maintainers',
            label: trans('dashboard.v2.preventative_maintenance.filter.maintainer'),
            options: maintainers.value,
            showSearchbar: true,
        },
        {
            type: 'multi-select',
            key: 'types',
            label: trans('assets.overview.filter.type'),
            options: types.value,
        },
        {
            type: 'date-range',
            label: trans('activities.overview.cell.date'),
            startKey: 'start',
            endKey: 'end',
            defaultStart: DateTime.now().minus({ days: 30 }).toISODate(),
            defaultEnd: DateTime.now().toISODate(),
            maxDate: DateTime.now().toISODate(),
        },
        {
            type: 'single-select',
            key: 'shift',
            label: trans('dashboard.v2.testing_compliance.shift'),
            options: shiftOptions.value,
            default: complianceShiftMeta.value?.current ?? null,
        },
    ].filter((f) => {
        if (currentDashboard.value === 'testing-compliance') {
            // Keep `shift` in the config even before its options load so FilterBar
            // registers it as a query-param field at mount — otherwise the value
            // never reaches the widgets' form data. FilterBar hides a single-select
            // with no options until the by-type widget publishes them.
            return f.key === 'locations' || f.key === 'shift';
        }
        if (currentDashboard.value === 'preventative-maintenance') {
            return f.key === 'locations' || f.key === 'maintainers' || f.key === 'types';
        }
        // `shift` is compliance-only; every other filter except maintainers shows.
        return f.key !== 'maintainers' && f.key !== 'shift';
    }),
}));

const form = useForm({
    widgets: initialWidgets(),
});

const editDashboard = () => {
    isGridEditmodeActive.value = true;
};

const toggleFilterbar = () => {
    if (queryParams.value?.form?.processing) {
        return;
    }
    filterIsOpen.value = !filterIsOpen.value;
};

const handleSubmit = () => {};

const refreshInterval = ref(null);
const gridStackRef = ref(null);
const lastUpdatedAt = ref(DateTime.now());

watch(
    () => selectedGrouping.value,
    (value) => filterBar.value?.updateValue('grouping', value),
    { immediate: true },
);

// Keep the shift filter valid as the shift metadata changes: default it to the
// airport's current shift when unset, and reset a stale selection (a key that's
// no longer offered after a scope change) so status lookups don't degrade to
// `unscheduled`.
watch(
    () => ({
        current: complianceShiftMeta.value?.current ?? null,
        keys: (complianceShiftMeta.value?.options ?? []).map((option) => option.value),
    }),
    ({ current, keys }) => {
        const selected = queryParams.value?.form?.data?.()?.shift ?? null;

        if (selected === null && current) {
            filterBar.value?.updateValue('shift', current);
        } else if (selected !== null && !keys.includes(selected)) {
            filterBar.value?.updateValue('shift', current ?? null);
        }
    },
);

onMounted(() => {
    refreshInterval.value = setInterval(() => {
        eventBus.$emit(events.dashboardWidgetRefresh);
        lastUpdatedAt.value = DateTime.now();
    }, 60000);
});

onBeforeUnmount(() => {
    if (refreshInterval.value) {
        clearInterval(refreshInterval.value);
    }
});
</script>
